import { useState, useEffect } from "react";
import SEO from "../seo";
import { APP_DATA, PLANS_PRICING } from "../constant_data";
const EntryPlan = () => {
  const [activeSection, setActiveSection] = useState("plan-required");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "plan-required",
        "entry-offer",
        "no-plan",
        "cancellation",
        "faqs",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-8">
      <SEO
        title="XPage Entry Plan – Publishing & ${APP_DATA.ENTRY_PRICE} for 14 Days"
        description="Activate an XPage subscription to publish your landing pages. Start with the ${APP_DATA.ENTRY_PRICE} entry offer for 14 days and unlock full access to your selected plan."
        keywords="XPage entry plan, ${APP_DATA.ENTRY_PRICE} for 14 days, publish landing page, XPage subscription, domain connection"
        canonical="https://help.xpage.ai/help/pricing-plans/Entry%20plan"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Main Title */}
        <div className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold">
            Publishing & ${APP_DATA.ENTRY_PRICE} Entry Offer
          </h1>
          <p className="text-sm text-systemSubTitle mt-1">
            To publish your landing page and make it accessible to customers,
            you must activate a subscription plan. You can freely build and edit
            your pages in draft mode, but going live requires an active plan.
          </p>
        </div>

        {/* Plan Required */}
        <div id="plan-required" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            Is a plan required to publish?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm">
              Yes. Activating a plan is required to take your store live. Until
              you subscribe, your landing pages remain unpublished and
              inaccessible to visitors.
            </p>
          </div>
        </div>

        {/* Entry Offer */}
        <div id="entry-offer" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            What is the ${APP_DATA.ENTRY_PRICE} for {APP_DATA.ENTRY_DAYS} Days
            offer?
          </h2>

          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm mb-3">
              You can choose any XPage plan and unlock full access for{" "}
              {APP_DATA.ENTRY_DAYS} days for just ${APP_DATA.ENTRY_PRICE}. Once
              activated, you can:
            </p>

            <ul className="space-y-2">
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-globe text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Publish your landing pages
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-link text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Connect your custom domain
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-shopping-cart text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Start receiving orders
                </span>
              </li>
              <li className="flex items-center">
                <div className="bg-core rounded-full w-6 h-6 flex items-center justify-center mr-3 flex-shrink-0">
                  <i className="fas fa-cogs text-white text-xs"></i>
                </div>
                <span className="text-systemLabel text-sm">
                  Use all features included in your selected plan
                </span>
              </li>
            </ul>

            <p className="text-systemLabel text-sm mt-4">
              After the {APP_DATA.ENTRY_DAYS}-day period, your subscription
              automatically continues at the standard monthly price of the plan
              you selected, unless you cancel before renewal.
            </p>
          </div>
        </div>

        {/* No Plan */}
        <div id="no-plan" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            What happens if I don’t choose a plan?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <ul className="space-y-2 text-systemLabel text-sm">
              <li>Your landing pages remain in draft mode</li>
              <li>They cannot be published</li>
              <li>Customers cannot access them</li>
            </ul>
            <p className="mt-3 text-systemLabel text-sm">
              To go live, you must activate a subscription plan.
            </p>
          </div>
        </div>

        {/* Cancellation */}
        <div id="cancellation" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-2">
            Can I cancel during the 14 days?
          </h2>
          <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
            <p className="text-systemLabel text-sm">
              Yes. You can cancel anytime during the {APP_DATA.ENTRY_DAYS}-day
              entry period. If you cancel, your landing pages will be
              unpublished once your access ends.
            </p>
          </div>
        </div>

        {/* FAQs */}
        <div id="faqs" className="mb-6 scroll-mt-20">
          <h2 className="text-systemTitle text-lg font-semibold mb-3">FAQs</h2>

          <div className="space-y-3">
            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Can I publish without activating a plan?
              </h3>
              <p className="text-systemLabel text-sm">
                No. Publishing and making your landing page accessible to
                customers requires an active subscription.
              </p>
            </div>

            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                Does the subscription renew automatically?
              </h3>
              <p className="text-systemLabel text-sm">
                Yes. After the {APP_DATA.ENTRY_DAYS}-day ${APP_DATA.ENTRY_PRICE}{" "}
                period, the subscription continues at the standard monthly price
                unless canceled before renewal.
              </p>
            </div>

            <div className="bg-systemBackground p-4 rounded-lg border border-systemBorder">
              <h3 className="text-systemTitle text-sm font-semibold mb-2">
                What happens to my pages if I cancel?
              </h3>
              <p className="text-systemLabel text-sm">
                Your landing pages will be unpublished once your access ends,
                but your content remains saved in your account.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            {[
              { id: "plan-required", label: "Plan Required" },
              {
                id: "entry-offer",
                label: `$${APP_DATA.ENTRY_PRICE} Entry Offer`,
              },
              { id: "no-plan", label: "Without a Plan" },
              { id: "cancellation", label: "Cancellation" },
              { id: "faqs", label: "FAQs" },
            ].map((item) => (
              <li key={item.id}>
                <a
                  href={`#${item.id}`}
                  className={`block py-1 transition-colors border-l-2 pl-3 ${
                    activeSection === item.id
                      ? "text-deepPurple font-semibold border-deepPurple"
                      : "border-transparent hover:text-deepPurple"
                  }`}
                >
                  {item.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default EntryPlan;
